import { t } from '@/i18n';

import type { LegacyAuthApi } from '../api/useLegacyCreativeApi';

/**
 * Minimal API surface required by postAsOwner / postAsOwnerOrSelf.
 * Method signatures (bivariant) allow callers with narrower payload types (e.g. LegacyCreativeApi)
 * to be passed without a TypeScript contravariance error.
 * Generic over TResponse so callers with typed APIs preserve their return type.
 */
type PostableApi<TResponse = unknown> = {
  post(path: string, payload: unknown): Promise<TResponse>;
  postByUser(path: string, payload: unknown, opts: { token: string; roleId: number }): Promise<TResponse>;
};

/**
 * Obtain a disguise token for acting on behalf of a campaign owner.
 * Used by admin flows (upload creatives, create landings, custom content).
 */
export const obtainDisguiseToken = async (authApi: Pick<LegacyAuthApi, 'post'>, ownerId: number): Promise<string> => {
  const res = await authApi.post(`/auth/disguise/${ownerId}`);
  return res.accessToken;
};

export const ADMIN_DISGUISE_ROLE_ID = 4;

export class DisguiseTokenError extends Error {
  constructor(message = 'Failed to obtain disguise token') {
    super(message);
    this.name = 'DisguiseTokenError';
  }
}

/**
 * User-facing message for DisguiseTokenError in form submission flows.
 *
 * A function, not a constant: it is display copy, and a module-level `t()` would resolve once at
 * import and keep the language the app booted in (see `src/i18n/CLAUDE.md`).
 */
export const disguiseTokenUserMessage = (): string => t('modules.common.disguiseTokenError');

/**
 * Post a request to the legacy API as the campaign owner using admin disguise.
 * Throws DisguiseTokenError if the token cannot be obtained.
 */
export const postAsOwner = async <TResponse = unknown>(
  api: PostableApi<TResponse>,
  authApi: LegacyAuthApi,
  ownerId: number,
  path: string,
  payload: unknown,
): Promise<TResponse> => {
  let token: string;
  try {
    token = await obtainDisguiseToken(authApi, ownerId);
  } catch {
    throw new DisguiseTokenError();
  }
  return api.postByUser(path, payload, { token, roleId: ADMIN_DISGUISE_ROLE_ID });
};

/**
 * Post a request as the campaign owner if admin, or as the current user otherwise.
 * Throws DisguiseTokenError if admin but auth API or owner ID is unavailable, or token fails.
 */
export const postAsOwnerOrSelf = async <TResponse = unknown>(
  api: PostableApi<TResponse>,
  authApi: LegacyAuthApi | undefined,
  isAdmin: boolean,
  ownerId: number | null | undefined,
  path: string,
  payload: unknown,
): Promise<TResponse> => {
  if (!isAdmin) {
    return api.post(path, payload);
  }
  if (!authApi || ownerId == null) {
    throw new DisguiseTokenError('Auth API or owner ID not available');
  }
  return postAsOwner(api, authApi, ownerId, path, payload);
};

/**
 * Make an arbitrary request to the legacy auth API as the campaign owner using admin disguise.
 * Throws DisguiseTokenError if the token cannot be obtained.
 */
export const requestAsOwner = async (
  authApi: LegacyAuthApi,
  ownerId: number,
  method: string,
  path: string,
  payload?: Record<string, unknown>,
): Promise<unknown> => {
  let token: string;
  try {
    token = await obtainDisguiseToken(authApi, ownerId);
  } catch {
    throw new DisguiseTokenError();
  }
  return authApi.request(method, path, payload ?? {}, {
    token,
    roleId: ADMIN_DISGUISE_ROLE_ID,
  });
};
